import { ChessTermType } from "@chesshotel/types";
import { Chess, swapColor, } from "@chesshotel/chess";
import { exchangeEvaluation, uci2move, move2uci } from "../util.js";
import { checkDefenceLossDiscoveredAttack, checkDefendable } from "./util.js";
const FILES = "abcdefgh";
function squareToCoords(square) {
    const x = FILES.indexOf(square[0]);
    const y = parseInt(square[1], 10) - 1;
    return { x, y };
}
function coordsToSquare(x, y) {
    if (x < 0 || x > 7 || y < 0 || y > 7)
        return null;
    return (FILES[x] + (y + 1));
}
function findFirstBoardPiece(chess, start, dx, dy) {
    const { x, y } = squareToCoords(start);
    let cx = x + dx;
    let cy = y + dy;
    while (true) {
        const sq = coordsToSquare(cx, cy);
        if (!sq)
            return null;
        const p = chess.getBoardPiece(sq);
        if (p)
            return p;
        cx += dx;
        cy += dy;
    }
}
function isSliderForDirection(piece, dx, dy) {
    if (dx === 0 || dy === 0) {
        return piece.type === "r" || piece.type === "q";
    }
    return piece.type === "b" || piece.type === "q";
}
function getBlockingPieceBestCapture(chess, square) {
    chess.changeTurn();
    const fenForEval = chess.fen();
    let best = undefined;
    const moves = chess.moves({ verbose: true, legal: true, square });
    for (const move of moves) {
        if (!move.captured)
            continue;
        const evaluation = exchangeEvaluation(fenForEval, move.to, {
            from: move.from,
        });
        if (evaluation.gain <= 0)
            continue;
        if (!best || evaluation.gain > best.evaluation.gain) {
            best = { move, evaluation: { gain: evaluation.gain } };
        }
    }
    chess.changeTurn();
    return best;
}
function secondHighestOf3(a, b, c) {
    if (a >= b) {
        if (b >= c)
            return b;
        return a >= c ? c : a;
    }
    else {
        if (a >= c)
            return a;
        return b >= c ? c : b;
    }
}
export function detectDiscoveredAttack(fen, moveStr) {
    const chess = new Chess(fen);
    const color = chess.turn();
    const opponentColor = swapColor(color);
    const moveObj = uci2move(moveStr);
    const blockingPiece = chess.getBoardPiece(moveObj.from);
    if (!blockingPiece)
        return null;
    const dirs = [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
        [1, 1],
        [1, -1],
        [-1, 1],
        [-1, -1],
    ];
    const potentialDiscoveredAttacks = [];
    for (const [dx, dy] of dirs) {
        const targetPiece = findFirstBoardPiece(chess, moveObj.from, dx, dy);
        if (!targetPiece || targetPiece.color !== opponentColor)
            continue;
        const attackingPiece = findFirstBoardPiece(chess, moveObj.from, -dx, -dy);
        if (!attackingPiece || attackingPiece.color !== color)
            continue;
        if (!isSliderForDirection(attackingPiece, dx, dy))
            continue;
        if (chess.attackedBy(targetPiece.square, moveObj.from, color)) {
            const exEval = exchangeEvaluation(chess.fen(), targetPiece.square, {
                from: moveObj.from,
            });
            if (exEval.gain > 0)
                continue;
        }
        potentialDiscoveredAttacks.push({
            attackingPiece,
            blockingPiece,
            targetPiece,
        });
    }
    if (potentialDiscoveredAttacks.length === 0)
        return null;
    let best = null;
    for (const attack of potentialDiscoveredAttacks) {
        const chess = new Chess(fen);
        const moveResult = chess.move(moveObj);
        if (!moveResult)
            continue;
        const { attackingPiece, targetPiece } = attack;
        if (moveResult.to == targetPiece.square) {
            continue;
        }
        const ray = chess.discoveredAttackRay(targetPiece.square, attackingPiece.square, color);
        if (!ray) {
            const evalResult = exchangeEvaluation(chess.fen(), attackingPiece.square);
            if (evalResult.gain > 0 ||
                (evalResult.gain == 0 && evalResult.moveSequence.length > 0)) {
                const isDefenceLoss = checkDefenceLossDiscoveredAttack(chess.fen(), move2uci(evalResult.moveSequence[0]), evalResult.gain);
                if (!isDefenceLoss) {
                    continue;
                }
            }
            const evalResult2 = exchangeEvaluation(chess.fen(), moveResult.to, {
                backtrackBadMoves: true,
            });
            if (evalResult2.gain > 0 ||
                (evalResult2.gain == 0 && evalResult2.moveSequence.length > 0)) {
                const isDefenceLoss = checkDefenceLossDiscoveredAttack(chess.fen(), move2uci(evalResult2.moveSequence[0]), evalResult2.gain);
                if (!isDefenceLoss) {
                    continue;
                }
            }
            const blockingPieceGain = evalResult2.gain;
            const bestBlockingPieceCapture = getBlockingPieceBestCapture(chess, moveObj.to);
            const bestBlockingPieceCaptureGain = (bestBlockingPieceCapture &&
                bestBlockingPieceCapture.evaluation.gain) ||
                0;
            const captureMove1 = uci2move((attackingPiece.square + targetPiece.square));
            const captures = [captureMove1];
            const blockingCapture = moveResult?.captured;
            const captureMove3 = bestBlockingPieceCapture && bestBlockingPieceCapture.move;
            if (captureMove3) {
                captures.push(captureMove3);
            }
            const inCheckNow = chess.inCheck();
            let replyMove = null;
            if (!inCheckNow && blockingCapture) {
                chess.changeTurn();
            }
            else {
                const { isDefendable, replyMove: defendReplyMove } = checkDefendable(chess.fen(), captures, false);
                replyMove = defendReplyMove;
                if (isDefendable && !blockingCapture) {
                    continue;
                }
                if (inCheckNow) {
                    if (!replyMove)
                        continue;
                    chess.move(replyMove);
                }
                else {
                    chess.changeTurn();
                }
            }
            let discoveredAttackGain = 0;
            if (targetPiece.type != "k") {
                const discoveredAttackEvalResult = exchangeEvaluation(chess.fen(), targetPiece.square, { from: attackingPiece.square, filteredByLoss: true });
                discoveredAttackGain = discoveredAttackEvalResult.gain;
                if (discoveredAttackEvalResult.gain <= 0) {
                    continue;
                }
            }
            else {
                discoveredAttackGain = 100;
            }
            const gain = secondHighestOf3(discoveredAttackGain, blockingPieceGain, bestBlockingPieceCaptureGain);
            if (gain > 0 && (!best || gain > best.gain)) {
                best = {
                    moves: [captureMove1, moveObj],
                    gain,
                    followMoves: captures.map((move) => move2uci(move)),
                    moveResult,
                };
            }
        }
    }
    if (!best)
        return null;
    const { moves, gain, moveResult, followMoves } = best;
    return {
        term: ChessTermType.DISCOVERED_ATTACK,
        mainPiece: chess.getBoardPiece(moveResult.to),
        move: moveResult,
        maxGain: gain,
        arrows: moves,
        followMoves,
    };
}
//# sourceMappingURL=discoveredAttack.js.map